import React, { useEffect, useState } from "react";
import { assistantApi } from "../../services/api";

function EventCard({ event }) {
  return (
    <a
      className="event-card"
      href={event.url}
      target="_blank"
      rel="noreferrer"
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="min-w-0">
          <div className="fw-medium">{event.title}</div>
          <div className="small text-muted mt-1 d-flex flex-wrap gap-x-3 gap-y-1">
            {event.when && (
              <span>
                <i className="bi bi-calendar3 me-1" aria-hidden="true" />
                {event.when}
              </span>
            )}
            {(event.where || event.virtual) && (
              <span>
                <i
                  className={`bi ${event.virtual ? "bi-camera-video" : "bi-geo-alt"} me-1`}
                  aria-hidden="true"
                />
                {event.where || "Virtual"}
              </span>
            )}
          </div>
          {event.snippet && (
            <p className="small text-muted mb-0 mt-2 event-card-snippet">
              {event.snippet}
            </p>
          )}
        </div>
        <i className="bi bi-box-arrow-up-right text-muted flex-shrink-0" />
      </div>
      <div className="small text-muted mt-2">
        {event.source === "cerebralvalley" ? "Cerebral Valley" : "Web search"}
      </div>
    </a>
  );
}

function EventsTab({ job }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const load = async () => {
    if (!job?.id || !job.company) {
      setLoading(false);
      setPayload({ configured: false, events: [], fallbacks: [] });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await assistantApi.events(job.id);
      setPayload(data);
    } catch (err) {
      setError(err.message || "Couldn't load events");
      setPayload(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when job/company changes
  }, [job.id, job.company]);

  const events = payload?.events || [];
  const fallbacks = payload?.fallbacks || [];
  const notConfigured = payload && !payload.configured;

  return (
    <div className="card">
      <div className="card-body">
        <div className="mb-3">
          <h2 className="h6 mb-1">Company events</h2>
          <p className="text-muted small mb-0">
            Upcoming hackathons, career fairs, meetups, webinars and similar
            events tied to {job.company || "this company"} - a way to meet
            people in person or virtually while your application is open. Results
            come from public web search
            {payload?.configured === false ? "" : " and community calendars"};
            they can be incomplete, so double-check dates before you go.
          </p>
        </div>

        {loading && (
          <div className="small text-muted">
            <span className="spinner-border spinner-border-sm me-1" /> Looking up
            events for {job.company}…
          </div>
        )}

        {error && (
          <div className="form-text text-danger mb-2">
            {error}{" "}
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-decoration-none align-baseline"
              onClick={load}
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && notConfigured && events.length === 0 && (
          <div className="alert alert-warning small py-2">
            Automatic event search isn't fully turned on for this server yet
            (needs a search API key). Use the links below to look them up
            yourself - same queries Plateo would run.
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-3">
            {events.map((event) => (
              <EventCard key={event.url} event={event} />
            ))}
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="small text-muted mb-3">
            No upcoming events turned up for {job.company || "this company"}{" "}
            yet. Try the searches below, or check their careers / events page.
          </div>
        )}

        {fallbacks.length > 0 && (
          <div className="border-top pt-3">
            <div className="small fw-medium mb-2">Search yourself</div>
            <div className="d-flex flex-wrap gap-2">
              {fallbacks.map((f) => (
                <a
                  key={f.url}
                  className="btn btn-sm btn-light"
                  href={f.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {f.label} <i className="bi bi-box-arrow-up-right ms-1" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventsTab;
